import dayjs from 'dayjs';

import React, { memo, useMemo } from 'react';

import { Timer } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';

import { formatDuration } from '@@common';

import { FlowInstanceRunModel } from '../../../api/api';
import FlowInstanceExecutionRun from './FlowInstanceExecutionRun';

interface FlowInstanceExecutionRunsProps {
	runs: FlowInstanceRunModel[];
	selectedStepId: string | null;
	onStepClick: (stepIndex: number, activityId?: string, stepId?: string) => void;
	getActivityType: (activityId: string | undefined) => string | undefined;
	getActivityData: (activityId: string | undefined) => any;
	onDeleteRun?: (runId: string) => void;
}

function FlowInstanceExecutionRuns({ runs, selectedStepId, onStepClick, getActivityType, getActivityData, onDeleteRun }: FlowInstanceExecutionRunsProps) {
	const runItems = useMemo(() => {
		return runs.map((run, runIndex) => {
			const currentSteps = run.steps || [];
			const previousRun = runIndex > 0 ? runs[runIndex - 1] : null;
			const previousSteps = previousRun?.steps || [];
			const selectedIndex = [...currentSteps].sort((left, right) => left.order - right.order).findIndex((step) => step.id === selectedStepId);

			return {
				run,
				runIndex,
				currentSteps,
				selectedStepIndex: selectedIndex < 0 ? null : selectedIndex,
				waitTimeMs:
					previousRun && currentSteps.length > 0 && previousSteps.length > 0
						? dayjs(currentSteps[0].timestamp).diff(dayjs(previousSteps[previousSteps.length - 1].timestamp), 'millisecond')
						: 0,
			};
		});
	}, [runs, selectedStepId]);

	return (
		<Box>
			<Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
				Execution Runs ({runs.length})
			</Typography>
			<Stack spacing={2}>
				{runItems.map(({ run, runIndex, currentSteps, waitTimeMs, selectedStepIndex }) => {
					return (
						<Box key={run.id}>
							{runIndex > 0 && waitTimeMs > 0 && (
								<Box sx={{ textAlign: 'center', my: 1 }}>
									<Chip
										icon={<Timer sx={{ fontSize: '0.85rem !important' }} />}
										label={`Wait time: ${formatDuration(waitTimeMs)}`}
										size="small"
										variant="outlined"
										sx={{
											fontSize: '0.7rem',
											height: '22px',
											bgcolor: 'background.paper',
											borderStyle: 'dashed',
											color: 'text.secondary',
										}}
									/>
								</Box>
							)}
							<FlowInstanceExecutionRun
								groupIndex={runIndex}
								executionId={run.id}
								runId={run.runId}
								steps={currentSteps}
								startIndex={0}
								selectedStepIndex={selectedStepIndex}
								onStepClick={onStepClick}
								getActivityType={getActivityType}
								getActivityData={getActivityData}
								onDeleteRun={onDeleteRun}
								isLastRun={runIndex === runs.length - 1}
							/>
						</Box>
					);
				})}
			</Stack>
		</Box>
	);
}

export default memo(FlowInstanceExecutionRuns);
